// Function index:
//   CreatureKind / CREATURE_PARTS — foe silhouettes (0 0 width 60 boxes,
//       ground y=60, facing right, currentColor only).
//   CREATURE_KEYWORDS — ordered substring table from npcs.archetype to a
//       creature; 'spider' before 'giant'-class words so "giant spider" is a
//       spider; fallback is 'humanoid' (bandits, guards, cultists…).
//   creatureForArchetype(archetype) — the mapping fn.

import type { PartDef } from './types.js';

// A creature that can hold gear declares where its hand is (same local space
// as the svg box, facing right). No anchor = no hands = the composer will
// never bolt a weapon onto it, whatever the cast says. Matches the server's
// gear-plausibility guard: beasts and birds take only natural/trophy gear.
export type CreatureDef = PartDef & { weaponAnchor?: { x: number; y: number } };

export type CreatureKind =
  | 'wolf' | 'bear' | 'boar' | 'troll' | 'goblin' | 'skeleton'
  | 'spider' | 'wyrm' | 'bird' | 'beast' | 'humanoid';

export const CREATURE_PARTS: Record<CreatureKind, CreatureDef> = {
  wolf: {
    width: 44,
    svg: '<ellipse cx="22" cy="46" rx="16" ry="7" fill="currentColor"/>' +
      '<polygon points="38,44 50,38 36,50" fill="currentColor" transform="translate(-6 0)"/>' +
      '<polygon points="36,38 40,32 38,40" fill="currentColor"/>' +
      '<line x1="12" y1="52" x2="10" y2="60" stroke="currentColor" stroke-width="3"/>' +
      '<line x1="20" y1="53" x2="20" y2="60" stroke="currentColor" stroke-width="3"/>' +
      '<line x1="30" y1="53" x2="32" y2="60" stroke="currentColor" stroke-width="3"/>' +
      '<path d="M6 44 q-6 -4 -4 -10" stroke="currentColor" stroke-width="3" fill="none"/>',
  },
  bear: {
    width: 48,
    svg: '<path d="M6 58 q-2 -22 12 -26 q16 -5 24 6 q6 9 4 20 Z" fill="currentColor"/>' +
      '<circle cx="42" cy="34" r="6" fill="currentColor"/>' +
      '<circle cx="45" cy="29" r="2" fill="currentColor"/>' +
      '<line x1="14" y1="56" x2="14" y2="60" stroke="currentColor" stroke-width="5"/>' +
      '<line x1="36" y1="56" x2="36" y2="60" stroke="currentColor" stroke-width="5"/>',
  },
  boar: {
    width: 42,
    svg: '<ellipse cx="20" cy="48" rx="16" ry="9" fill="currentColor"/>' +
      '<polygon points="34,46 44,48 34,53" fill="currentColor"/>' +
      '<line x1="40" y1="50" x2="44" y2="46" stroke="currentColor" stroke-width="1.8"/>' +
      '<line x1="12" y1="55" x2="11" y2="60" stroke="currentColor" stroke-width="3"/>' +
      '<line x1="28" y1="55" x2="29" y2="60" stroke="currentColor" stroke-width="3"/>',
  },
  troll: {
    width: 52,
    svg: '<path d="M8 60 q-4 -34 12 -42 q20 -8 26 10 q4 12 2 32 Z" fill="currentColor"/>' +
      '<circle cx="28" cy="12" r="8" fill="currentColor"/>' +
      '<line x1="10" y1="30" x2="-2 " y2="42" stroke="currentColor" stroke-width="5"/>' +
      '<path d="M-2 42 l-4 14" stroke="currentColor" stroke-width="4"/>',
    weaponAnchor: { x: 40, y: 26 },
  },
  goblin: {
    width: 34,
    svg: '<rect x="12" y="32" width="9" height="16" rx="4" fill="currentColor"/>' +
      '<circle cx="16.5" cy="27" r="5" fill="currentColor"/>' +
      '<polygon points="11,25 6,21 12,28" fill="currentColor"/><polygon points="22,25 27,21 21,28" fill="currentColor"/>' +
      '<line x1="14" y1="48" x2="12" y2="60" stroke="currentColor" stroke-width="3"/>' +
      '<line x1="19" y1="48" x2="21" y2="60" stroke="currentColor" stroke-width="3"/>' +
      '<line x1="21" y1="36" x2="30" y2="30" stroke="currentColor" stroke-width="2"/>',
    weaponAnchor: { x: 30, y: 30 },
  },
  skeleton: {
    width: 36,
    svg: '<rect x="15" y="22" width="6" height="18" rx="2" fill="currentColor"/>' +
      '<circle cx="18" cy="15" r="5.5" fill="currentColor"/>' +
      '<line x1="12" y1="26" x2="24" y2="26" stroke="currentColor" stroke-width="2"/>' +
      '<line x1="12" y1="31" x2="24" y2="31" stroke="currentColor" stroke-width="2"/>' +
      '<line x1="16" y1="40" x2="14" y2="60" stroke="currentColor" stroke-width="2.5"/>' +
      '<line x1="20" y1="40" x2="22" y2="60" stroke="currentColor" stroke-width="2.5"/>',
    weaponAnchor: { x: 26, y: 26 },
  },
  spider: {
    width: 46,
    svg: '<ellipse cx="24" cy="44" rx="12" ry="8" fill="currentColor"/><circle cx="36" cy="42" r="5" fill="currentColor"/>' +
      '<path d="M14 42 l-10 -8 M16 46 l-12 0 M18 50 l-10 8" stroke="currentColor" stroke-width="2" fill="none"/>' +
      '<path d="M32 48 l8 6 M34 44 l12 2 M34 40 l10 -6" stroke="currentColor" stroke-width="2" fill="none"/>',
  },
  wyrm: {
    width: 54,
    svg: '<path d="M4 56 q10 -18 24 -14 q16 4 18 -10 q2 -10 -6 -14" fill="none" stroke="currentColor" stroke-width="7"/>' +
      '<polygon points="40,14 52,10 44,22" fill="currentColor"/>' +
      '<path d="M22 40 l-6 -16 l14 8 Z" fill="currentColor"/>',
  },
  bird: {
    width: 34,
    svg: '<ellipse cx="16" cy="40" rx="9" ry="6" fill="currentColor"/>' +
      '<circle cx="26" cy="35" r="4" fill="currentColor"/><polygon points="29,35 35,36 29,38" fill="currentColor"/>' +
      '<path d="M12 38 l-8 -10 l14 4 Z" fill="currentColor"/>' +
      '<line x1="14" y1="46" x2="14" y2="60" stroke="currentColor" stroke-width="2"/>' +
      '<line x1="19" y1="46" x2="19" y2="60" stroke="currentColor" stroke-width="2"/>',
  },
  beast: {
    width: 46,
    svg: '<ellipse cx="22" cy="45" rx="15" ry="8" fill="currentColor"/>' +
      '<circle cx="37" cy="38" r="5" fill="currentColor"/>' +
      '<line x1="12" y1="52" x2="11" y2="60" stroke="currentColor" stroke-width="3"/>' +
      '<line x1="30" y1="52" x2="31" y2="60" stroke="currentColor" stroke-width="3"/>' +
      '<path d="M8 42 q-5 -3 -4 -8" stroke="currentColor" stroke-width="2.5" fill="none"/>',
  },
  humanoid: {
    width: 40,
    svg: '<rect x="15" y="20" width="10" height="23" rx="4" fill="currentColor"/>' +
      '<path d="M14 13 a6 6 0 0 1 12 0 l-1 6 h-10 Z" fill="currentColor"/>' +
      '<line x1="17" y1="43" x2="15" y2="60" stroke="currentColor" stroke-width="3.5"/>' +
      '<line x1="23" y1="43" x2="25" y2="60" stroke="currentColor" stroke-width="3.5"/>' +
      '<line x1="25" y1="27" x2="33" y2="21" stroke="currentColor" stroke-width="2.5"/>',
    weaponAnchor: { x: 33, y: 21 },
  },
};

export const CREATURE_KEYWORDS: ReadonlyArray<[string[], CreatureKind]> = [
  // spider before the giant-class words: "giant spider" is a spider.
  [['spider'], 'spider'],
  [['wolf', 'hound', 'dog'], 'wolf'],
  [['bear'], 'bear'],
  [['boar', 'pig'], 'boar'],
  [['troll', 'ogre', 'giant'], 'troll'],
  [['goblin', 'kobold', 'imp'], 'goblin'],
  [['skelet', 'undead', 'wight', 'ghoul', 'zombie'], 'skeleton'],
  [['wyrm', 'drake', 'dragon', 'serpent'], 'wyrm'],
  [['bird', 'hawk', 'crow', 'raven', 'harpy'], 'bird'],
  [['beast', 'lion', 'cat', 'stag', 'elk', 'rat'], 'beast'],
];

export function creatureForArchetype(archetype: string | null | undefined): CreatureKind {
  const a = (archetype ?? '').toLowerCase();
  for (const [keywords, kind] of CREATURE_KEYWORDS) {
    if (keywords.some((k) => a.includes(k))) return kind;
  }
  return 'humanoid';
}
